import { Component, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs";

import { AuthModel, EXPLICIT_SELECT_ORGANISATION } from "@intocare/auth-data";
import { ENVIRONMENT_TOKEN } from "@intocare/environments";
import { CONTEXT_TOKEN } from "@intocare/util";

@Component({
	selector: "ic-no-permissions",
	templateUrl: "./no-permissions.component.html",
	styleUrls: ["./no-permissions.component.scss"],
	standalone: false,
})
export class NoPermissionsComponent {
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly authModel = inject(AuthModel);
	private readonly environment = inject(ENVIRONMENT_TOKEN);
	private readonly contextService = inject(CONTEXT_TOKEN);

	private isLoggedIn = toSignal(this.authModel.isLoggedIn$);
	private isNewLoginRequired = computed(() => !!this.route.snapshot.queryParams.reset || !this.isLoggedIn());
	private readonly organisation = computed(() => {
		if (this.environment.platform === "hq") {
			return this.contextService.context().hq;
		} else {
			return `${this.contextService.context().hq}.${this.contextService.context().currentSite.home}`;
		}
	});

	protected buttonLabel = computed(() => {
		const isNewLoginRequired = this.isNewLoginRequired();

		return isNewLoginRequired ? "actions.close" : "actions.backToDashboard";
	});
	protected noPermissionsLabel = this.determineNoPermissionsLabel();

	private goBack() {
		this.authModel
			.logOut(false, { redirect: false })
			.pipe(
				finalize(() => {
					this.router
						.navigate(["select-organisation"], { state: { [EXPLICIT_SELECT_ORGANISATION]: true } })
						.then(() => document.location.reload());
				}),
			)
			.subscribe();
	}

	private goToDashboard() {
		this.router.navigate([`${this.organisation().split(".").join("/")}/dashboard`]);
	}

	protected navigate() {
		if (this.isNewLoginRequired()) {
			this.goBack();
		} else {
			this.goToDashboard();
		}
	}

	private determineNoPermissionsLabel(): string {
		if (this.isNewLoginRequired()) {
			if (this.environment.platform === "hq") {
				return "error.no_permissions_for_hq";
			} else if (this.environment.platform === "home") {
				return "error.no_permissions_for_portal";
			}
		}
		return "error.no_permissions_for_this_page";
	}
}
